/**
 * Transport cost formula breakdown (Excel rows 21+):
 * - Valoare marfă = preț listă × cantitate × (1 − discount%/100) — discount manual
 * - Convertor (E22): amount_foreign * converter → EUR (e.g. USD freight)
 * - Fee lines: fixed sum, converted sum (E21*E22), or % of selected bases
 * - Taxe vamale typical: (goods + naval) * 6.5%
 * - Rate = total fees / goods value
 */

export const TRANSPORT_COST_MODE_PERCENT = 'percent';
export const TRANSPORT_COST_MODE_FORMULA = 'formula';

export const TRANSPORT_LINE_TYPE_FIXED = 'fixed';
export const TRANSPORT_LINE_TYPE_CONVERTED = 'converted';
export const TRANSPORT_LINE_TYPE_PERCENT = 'percent';

export const TRANSPORT_LINE_ROLE_CUSTOMS = 'customs';

export const TRANSPORT_CURRENCY_OPTIONS = [
    { title: 'USD', value: 'usd' },
    { title: 'EUR', value: 'euro' },
    { title: 'RON', value: 'ron' },
    { title: 'PLN', value: 'pln' },
];

let transportLineKeySeq = 0;

/**
 * @param {unknown} value
 * @returns {number}
 */
function parseNumericInput(value) {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    let normalized = String(value).trim().replace(/\s/g, '');
    const hasComma = normalized.includes(',');
    const hasDot = normalized.includes('.');
    if (hasComma && hasDot) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else if (hasComma) {
        normalized = normalized.replace(',', '.');
    } else if (hasDot && /^[+-]?\d{1,3}(\.\d{3})+$/.test(normalized)) {
        const integerPart = normalized.replace(/^[+-]/, '').split('.')[0];
        if (integerPart !== '0') {
            normalized = normalized.replace(/\./g, '');
        }
    }
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * @param {number} value
 * @param {number} [decimals]
 * @returns {number}
 */
function roundMoney(value, decimals = 2) {
    if (!Number.isFinite(value)) {
        return 0;
    }
    const factor = 10 ** decimals;
    return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * @param {unknown} value
 * @param {number} [decimals]
 * @returns {string}
 */
function formatNumericInputValue(value, decimals = 2) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const numeric = typeof value === 'number' ? value : parseNumericInput(value);
    if (!Number.isFinite(numeric)) {
        return '';
    }
    return numeric.toLocaleString('ro-RO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
    });
}

/**
 * @param {unknown} value
 * @param {number} [decimals]
 * @returns {string}
 */
function ratioToPercentageInput(value, decimals = 2) {
    const ratio = parseNumericInput(value);
    if (!(ratio > 0) && ratio !== 0) {
        return '';
    }
    return formatNumericInputValue(ratio * 100, decimals);
}

/**
 * @param {string} currency
 * @returns {string}
 */
export function normalizeCurrencyCode(currency) {
    const normalized = String(currency || '').trim().toLowerCase();
    if (normalized === 'eur' || normalized === 'euro') {
        return 'euro';
    }
    if (normalized === 'usd' || normalized === 'dolari' || normalized === 'dollar') {
        return 'usd';
    }
    if (normalized === 'pln') {
        return 'pln';
    }
    if (normalized === 'ron' || normalized === 'leu') {
        return 'ron';
    }
    return normalized || 'euro';
}

/**
 * Ron-per-unit rates from pricing settings → cross rate from→to.
 * Excel "Convertor" is typically USD→EUR ≈ usdRon / eurRon (e.g. 0.86).
 *
 * @param {string} fromCurrency
 * @param {string} toCurrency
 * @param {{
 *   exchangeRateEur?: unknown,
 *   exchangeRateUsd?: unknown,
 *   exchangeRatePln?: unknown,
 *   exchangeRateRon?: unknown,
 * }} [pricingSettings]
 * @returns {number}
 */
export function resolveTransportConverterRate(
    fromCurrency,
    toCurrency,
    pricingSettings = {},
) {
    const from = normalizeCurrencyCode(fromCurrency);
    const to = normalizeCurrencyCode(toCurrency);
    if (!from || !to || from === to) {
        return 1;
    }

    const eur = parseNumericInput(pricingSettings.exchangeRateEur) || 5.35;
    const usd = parseNumericInput(pricingSettings.exchangeRateUsd) || 4.8;
    const pln = parseNumericInput(
        pricingSettings.exchangeRatePln ?? pricingSettings.exchangeRateRon,
    ) || 1.3;

    /** @type {Record<string, number>} */
    const ronPerUnit = {
        euro: eur,
        usd,
        pln,
        ron: 1,
    };
    const fromRon = ronPerUnit[from];
    const toRon = ronPerUnit[to];
    if (!(fromRon > 0) || !(toRon > 0)) {
        return 1;
    }
    return roundMoney(fromRon / toRon, 6);
}

/**
 * @returns {string}
 */
export function createTransportCostLineKey() {
    transportLineKeySeq += 1;
    return `tcl-${Date.now()}-${transportLineKeySeq}`;
}

/**
 * @param {unknown} type
 * @returns {string}
 */
function normalizeLineType(type) {
    if (type === TRANSPORT_LINE_TYPE_PERCENT) {
        return TRANSPORT_LINE_TYPE_PERCENT;
    }
    if (type === TRANSPORT_LINE_TYPE_CONVERTED) {
        return TRANSPORT_LINE_TYPE_CONVERTED;
    }
    return TRANSPORT_LINE_TYPE_FIXED;
}

/**
 * @param {Partial<{
 *   key?: string,
 *   label?: string,
 *   type?: string,
 *   value?: unknown,
 *   baseGoodsValue?: boolean,
 *   baseLineKeys?: string[],
 *   role?: string,
 *   enabled?: boolean,
 *   sourceAmount?: unknown,
 *   sourceCurrency?: string,
 *   converterUsed?: unknown,
 * }>} [overrides]
 */
export function createTransportCostLine(overrides = {}) {
    const sourceCurrencyRaw = String(overrides.sourceCurrency || '').trim();
    return {
        key: overrides.key || createTransportCostLineKey(),
        label: String(overrides.label ?? '').trim(),
        type: normalizeLineType(overrides.type),
        value: overrides.value === null || overrides.value === undefined
            ? ''
            : String(overrides.value),
        baseGoodsValue: overrides.baseGoodsValue !== false,
        baseLineKeys: Array.isArray(overrides.baseLineKeys)
            ? overrides.baseLineKeys.map((key) => String(key))
            : [],
        role: String(overrides.role || '').trim(),
        enabled: overrides.enabled !== false,
        // Provenance for amounts added via currency conversion (audit / display).
        sourceAmount: overrides.sourceAmount === null || overrides.sourceAmount === undefined
            ? ''
            : String(overrides.sourceAmount),
        sourceCurrency: sourceCurrencyRaw
            ? normalizeCurrencyCode(sourceCurrencyRaw)
            : '',
        converterUsed: overrides.converterUsed === null || overrides.converterUsed === undefined
            ? ''
            : String(overrides.converterUsed),
    };
}

/**
 * @param {unknown} line
 * @returns {boolean}
 */
export function isCustomsDutyLine(line) {
    if (!line || typeof line !== 'object') {
        return false;
    }
    const role = String(/** @type {{ role?: string }} */ (line).role || '').trim();
    if (role === TRANSPORT_LINE_ROLE_CUSTOMS) {
        return true;
    }
    return /vamal/i.test(String(/** @type {{ label?: string }} */ (line).label || '').trim());
}

/**
 * @param {Partial<{
 *   key?: string,
 *   value?: unknown,
 *   enabled?: boolean,
 *   baseLineKeys?: string[],
 * }>} [overrides]
 */
export function createDefaultCustomsDutyLine(overrides = {}) {
    return createTransportCostLine({
        label: 'taxe vamale',
        type: TRANSPORT_LINE_TYPE_PERCENT,
        value: '6.5',
        baseGoodsValue: true,
        baseLineKeys: [],
        role: TRANSPORT_LINE_ROLE_CUSTOMS,
        enabled: false,
        ...overrides,
    });
}

/**
 * Keep a single customs-duty row pinned at the bottom (never removed).
 *
 * @param {ReturnType<typeof createDefaultTransportCostBreakdown>} breakdown
 */
export function ensureCustomsDutyLine(breakdown) {
    const lines = Array.isArray(breakdown.lines) ? breakdown.lines : [];
    const existing = lines.filter((line) => isCustomsDutyLine(line));
    const others = lines.filter((line) => !isCustomsDutyLine(line));
    const source = existing[0] || createDefaultCustomsDutyLine();
    const customs = createTransportCostLine({
        ...source,
        role: TRANSPORT_LINE_ROLE_CUSTOMS,
        label: String(source.label || 'taxe vamale').trim() || 'taxe vamale',
        type: TRANSPORT_LINE_TYPE_PERCENT,
        enabled: source.enabled !== false,
    });
    breakdown.lines = [...others, customs];
    return breakdown;
}

/**
 * Amount/fixed lines first, customs duty always last.
 *
 * @param {ReturnType<typeof createTransportCostLine>[]} lines
 */
export function orderTransportCostLines(lines) {
    const amounts = [];
    /** @type {ReturnType<typeof createTransportCostLine>|null} */
    let customs = null;
    (lines || []).forEach((line) => {
        if (isCustomsDutyLine(line)) {
            customs = createTransportCostLine({
                ...line,
                role: TRANSPORT_LINE_ROLE_CUSTOMS,
            });
        } else {
            amounts.push(createTransportCostLine(line));
        }
    });
    if (!customs) {
        customs = createDefaultCustomsDutyLine();
    }
    return [...amounts, customs];
}

/**
 * @param {Partial<{
 *   mode?: string,
 *   goodsValue?: unknown,
 *   goodsQuantity?: unknown,
 *   goodsDiscount?: unknown,
 *   goodsValueExtra?: unknown,
 *   goodsValueIsManual?: unknown,
 *   rateIsManual?: unknown,
 *   converter?: unknown,
 *   converterFromCurrency?: unknown,
 *   converterToCurrency?: unknown,
 *   lines?: unknown[],
 * }>} [overrides]
 */
export function createDefaultTransportCostBreakdown(overrides = {}) {
    const mode = overrides.mode === TRANSPORT_COST_MODE_FORMULA
        ? TRANSPORT_COST_MODE_FORMULA
        : TRANSPORT_COST_MODE_PERCENT;
    const lines = Array.isArray(overrides.lines)
        ? overrides.lines.map((line) => createTransportCostLine(line || {}))
        : [];
    const breakdown = {
        mode,
        goodsValue: overrides.goodsValue === null || overrides.goodsValue === undefined
            ? ''
            : String(overrides.goodsValue),
        goodsQuantity: overrides.goodsQuantity === null || overrides.goodsQuantity === undefined
            ? ''
            : String(overrides.goodsQuantity),
        goodsDiscount: overrides.goodsDiscount === null || overrides.goodsDiscount === undefined
            ? ''
            : String(overrides.goodsDiscount),
        goodsValueExtra: overrides.goodsValueExtra === null || overrides.goodsValueExtra === undefined
            ? ''
            : String(overrides.goodsValueExtra),
        goodsValueIsManual: Boolean(overrides.goodsValueIsManual),
        rateIsManual: Boolean(overrides.rateIsManual),
        converter: overrides.converter === null || overrides.converter === undefined
            ? ''
            : String(overrides.converter),
        converterFromCurrency: normalizeCurrencyCode(
            overrides.converterFromCurrency || 'usd',
        ),
        converterToCurrency: normalizeCurrencyCode(
            overrides.converterToCurrency || 'euro',
        ),
        lines,
    };
    ensureCustomsDutyLine(breakdown);
    return breakdown;
}

/**
 * @param {unknown} raw
 */
export function normalizeTransportCostBreakdown(raw) {
    if (!raw || typeof raw !== 'object') {
        return createDefaultTransportCostBreakdown();
    }
    return createDefaultTransportCostBreakdown(/** @type {Record<string, unknown>} */ (raw));
}

/**
 * @param {unknown} raw
 */
export function cloneTransportCostBreakdown(raw) {
    const normalized = normalizeTransportCostBreakdown(raw);
    return createDefaultTransportCostBreakdown({
        mode: normalized.mode,
        goodsValue: normalized.goodsValue,
        goodsQuantity: normalized.goodsQuantity,
        goodsDiscount: normalized.goodsDiscount,
        goodsValueExtra: normalized.goodsValueExtra,
        goodsValueIsManual: normalized.goodsValueIsManual,
        rateIsManual: normalized.rateIsManual,
        converter: normalized.converter,
        converterFromCurrency: normalized.converterFromCurrency,
        converterToCurrency: normalized.converterToCurrency,
        lines: normalized.lines.map((line) => ({
            ...line,
            baseLineKeys: [...line.baseLineKeys],
        })),
    });
}

/**
 * Manual discount % for goods value (0–100). Not taken from product discounts.
 * Negatives and non-numeric values resolve to 0 for calculation; UI validation
 * should reject negatives separately.
 *
 * @param {unknown} value
 * @returns {number}
 */
export function parseTransportGoodsDiscountPercent(value) {
    const parsed = parseNumericInput(value);
    if (!(parsed > 0)) {
        return 0;
    }
    if (parsed > 100) {
        return 100;
    }
    return parsed;
}

/**
 * True when auto goods value can be derived from list price × quantity.
 *
 * @param {unknown} rawBreakdown
 * @param {{ supplierListPriceNew?: unknown }} [context]
 * @returns {boolean}
 */
export function canAutoCalculateTransportGoodsValue(rawBreakdown, context = {}) {
    const breakdown = normalizeTransportCostBreakdown(rawBreakdown);
    const price = parseNumericInput(context.supplierListPriceNew);
    const qty = parseNumericInput(breakdown.goodsQuantity);
    return price > 0 && qty > 0;
}

/**
 * Valoare marfă = preț listă furnizor × cantitate × (1 − discount%/100).
 * Dacă goodsValueIsManual, se folosește valoarea introdusă direct.
 * Când prețul și cantitatea sunt disponibile, rezultatul calculat (inclusiv 0
 * la discount 100%) are prioritate — nu se folosește o valoare stocată veche.
 * Cantitate explicită 0 (sau negativă) → 0, fără fallback la valoarea veche.
 *
 * @param {unknown} rawBreakdown
 * @param {{ supplierListPriceNew?: unknown }} [context]
 * @returns {number}
 */
export function resolveEffectiveTransportGoodsValue(rawBreakdown, context = {}) {
    const breakdown = normalizeTransportCostBreakdown(rawBreakdown);
    if (breakdown.goodsValueIsManual) {
        return parseNumericInput(breakdown.goodsValue);
    }
    const price = parseNumericInput(context.supplierListPriceNew);
    const qty = parseNumericInput(breakdown.goodsQuantity);
    const qtyRaw = String(breakdown.goodsQuantity ?? '').trim();
    if (qtyRaw !== '' && !(qty > 0)) {
        return 0;
    }
    if (!(price > 0 && qty > 0)) {
        return parseNumericInput(breakdown.goodsValue);
    }
    const discountPercent = parseTransportGoodsDiscountPercent(
        breakdown.goodsDiscount,
    );
    const factor = 1 - (discountPercent / 100);
    return roundMoney(price * qty * factor, 4);
}

/**
 * Format valoare marfă for the input field. Prefer 2 decimals, but keep up to 4
 * when a high discount leaves a small residual that would otherwise display as 0.
 *
 * @param {number} value
 * @returns {string}
 */
function formatTransportGoodsValueInput(value) {
    if (!(value > 0)) {
        return formatNumericInputValue(0, 2);
    }
    const as2 = formatNumericInputValue(value, 2);
    if (parseNumericInput(as2) > 0) {
        return as2;
    }
    return formatNumericInputValue(value, 4);
}

/**
 * @param {unknown} rawBreakdown
 * @param {{ supplierListPriceNew?: unknown }} [context]
 * @returns {ReturnType<typeof createDefaultTransportCostBreakdown>}
 */
export function applyAutoTransportGoodsValue(rawBreakdown, context = {}) {
    const breakdown = normalizeTransportCostBreakdown(rawBreakdown);
    if (breakdown.goodsValueIsManual) {
        return breakdown;
    }
    const qtyRaw = String(breakdown.goodsQuantity ?? '').trim();
    const qty = parseNumericInput(breakdown.goodsQuantity);
    if (qtyRaw !== '' && !(qty > 0)) {
        breakdown.goodsValue = formatTransportGoodsValueInput(0);
        return breakdown;
    }
    if (!canAutoCalculateTransportGoodsValue(breakdown, context)) {
        return breakdown;
    }
    const goodsValue = resolveEffectiveTransportGoodsValue(breakdown, context);
    breakdown.goodsValue = formatTransportGoodsValueInput(goodsValue);
    return breakdown;
}

/**
 * Field-level validation for transport goods inputs (Romanian messages).
 * Empty discount is allowed (treated as 0%). Quantity and goods value must be > 0.
 *
 * @param {unknown} rawBreakdown
 * @param {{ supplierListPriceNew?: unknown }} [context]
 * @returns {{
 *   goodsQuantity: string|null,
 *   goodsDiscount: string|null,
 *   goodsValue: string|null,
 *   valid: boolean,
 * }}
 */
export function validateTransportGoodsFields(rawBreakdown, context = {}) {
    const breakdown = normalizeTransportCostBreakdown(rawBreakdown);
    /** @type {{ goodsQuantity: string|null, goodsDiscount: string|null, goodsValue: string|null }} */
    const errors = {
        goodsQuantity: null,
        goodsDiscount: null,
        goodsValue: null,
    };

    const qtyRaw = String(breakdown.goodsQuantity ?? '').trim();
    const qty = parseNumericInput(breakdown.goodsQuantity);
    if (!qtyRaw) {
        errors.goodsQuantity =
            'Cantitatea este obligatorie. Introduceți o valoare mai mare decât 0 (ex.: numărul de bucăți din comandă).';
    } else if (!(qty > 0)) {
        errors.goodsQuantity =
            'Cantitatea invalidă. Folosiți o valoare strict mai mare decât 0 — 0 nu permite calculul valorii mărfii.';
    }

    const discountRaw = String(breakdown.goodsDiscount ?? '').trim();
    const discount = discountRaw === '' ? 0 : parseNumericInput(breakdown.goodsDiscount);
    if (discountRaw !== '') {
        if (discount < 0) {
            errors.goodsDiscount =
                'Discountul nu poate fi negativ (ex.: -100). Lăsați gol pentru 0% sau introduceți o valoare între 0 și 99,99.';
        } else if (discount > 100) {
            errors.goodsDiscount =
                'Discountul nu poate depăși 100%. Valoarea maximă utilă este sub 100% (ex.: 10 sau 99,99).';
        } else if (discount >= 100) {
            errors.goodsDiscount =
                'Discount 100% face valoarea mărfii 0 (preț × cantitate × 0), deci % transport nu se poate calcula. Folosiți un discount sub 100% (ex.: 99,99) sau lăsați câmpul gol.';
        }
    }

    const goodsValue = resolveEffectiveTransportGoodsValue(breakdown, context);
    // Practical money floor: residual after a near-100% discount can round to ~0.
    const goodsValueDisplay = parseNumericInput(formatTransportGoodsValueInput(goodsValue));
    if (!(goodsValue > 0) || !(goodsValueDisplay > 0)) {
        if (errors.goodsQuantity || errors.goodsDiscount) {
            // Primary field error already explains the problem.
        } else if (!(parseNumericInput(context.supplierListPriceNew) > 0)) {
            errors.goodsValue =
                'Lipsește prețul listă furnizor din produs. Completați-l în builder pentru a calcula automat valoarea mărfii.';
        } else if (discount > 0 && discount < 100) {
            errors.goodsDiscount =
                `Discountul de ${formatNumericInputValue(discount, 2)}% este prea mare pentru prețul × cantitatea curente: restul (valoarea mărfii) ajunge ~0 și % transport nu se poate calcula. Reduceți discountul.`;
            errors.goodsValue =
                'Valoarea mărfii trebuie să fie mai mare decât 0. Este baza pentru % transport (= total costuri ÷ valoare marfă).';
        } else {
            errors.goodsValue =
                'Valoarea mărfii trebuie să fie mai mare decât 0. Completați cantitatea (și eventual discountul) sau introduceți manual o valoare pozitivă.';
        }
    }

    return {
        ...errors,
        valid: !errors.goodsQuantity && !errors.goodsDiscount && !errors.goodsValue,
    };
}

/**
 * @param {ReturnType<typeof createTransportCostLine>} line
 * @param {number} goodsValue
 * @param {number} converter
 * @param {Map<string, number>} amountsByKey
 * @returns {number}
 */
function resolveLineAmount(line, goodsValue, converter, amountsByKey) {
    if (line.enabled === false) {
        return 0;
    }
    const numericValue = parseNumericInput(line.value);
    if (!(numericValue > 0)) {
        return 0;
    }
    if (line.type === TRANSPORT_LINE_TYPE_CONVERTED) {
        const factor = converter > 0 ? converter : 1;
        return roundMoney(numericValue * factor, 4);
    }
    if (line.type !== TRANSPORT_LINE_TYPE_PERCENT) {
        return roundMoney(numericValue, 4);
    }

    let base = 0;
    if (line.baseGoodsValue !== false) {
        base += goodsValue;
    }
    const baseKeys = resolvePercentBaseLineKeys(line, amountsByKey);
    baseKeys.forEach((key) => {
        base += amountsByKey.get(String(key)) || 0;
    });
    if (!(base > 0)) {
        return 0;
    }
    return roundMoney(base * (numericValue / 100), 4);
}

/**
 * Excel F29 = (F20+F21)*6.5%: goods + main freight only.
 * If baseLineKeys is empty, fall back to the largest amount line.
 *
 * @param {{ baseLineKeys?: string[] }} line
 * @param {Map<string, number>} amountsByKey
 * @returns {string[]}
 */
function resolvePercentBaseLineKeys(line, amountsByKey) {
    const explicit = (line.baseLineKeys || [])
        .map((key) => String(key))
        .filter(Boolean);
    if (explicit.length) {
        return explicit;
    }
    let maxKey = '';
    let maxAmount = -1;
    amountsByKey.forEach((amount, key) => {
        if (amount > maxAmount) {
            maxAmount = amount;
            maxKey = String(key);
        }
    });
    return maxKey ? [maxKey] : [];
}

/**
 * Non-percent lines usable as bases for a percent fee (Excel: F20+F21).
 *
 * @param {ReturnType<typeof createDefaultTransportCostBreakdown>} breakdown
 * @param {string} [excludeKey]
 */
export function transportAmountBaseLines(breakdown, excludeKey = '') {
    return (breakdown?.lines || []).filter((line) => (
        line.type !== TRANSPORT_LINE_TYPE_PERCENT
        && line.enabled !== false
        && line.key !== excludeKey
    ));
}

/**
 * Ensure percent lines have an explicit Excel-like base (goods + main freight).
 *
 * @param {ReturnType<typeof createDefaultTransportCostBreakdown>} breakdown
 */
export function ensurePercentFeeBases(breakdown) {
    const amountLines = transportAmountBaseLines(breakdown);
    if (!amountLines.length) {
        return breakdown;
    }
    const preferred = amountLines.find((line) => (
        /vamal|naval|freight|20\s*hc|40\s*hc|^transport\s+\d+$/i.test(
            String(line.label || '').trim(),
        )
    )) || amountLines.reduce((best, line) => {
        const bestValue = parseNumericInput(best.value);
        const lineValue = parseNumericInput(line.value);
        return lineValue > bestValue ? line : best;
    }, amountLines[0]);

    (breakdown.lines || []).forEach((line) => {
        if (line.type !== TRANSPORT_LINE_TYPE_PERCENT) {
            return;
        }
        if (line.baseGoodsValue !== false) {
            line.baseGoodsValue = true;
        }
        if (!(line.baseLineKeys || []).length) {
            line.baseLineKeys = [preferred.key];
        }
    });
    return breakdown;
}

/**
 * @param {unknown} rawBreakdown
 * @param {{ supplierListPriceNew?: unknown }} [context]
 * @returns {{
 *   goodsValue: number,
 *   converter: number,
 *   lineAmounts: { key: string, label: string, type: string, amount: number }[],
 *   total: number,
 *   rate: number,
 * }}
 */
export function calculateTransportCostBreakdown(rawBreakdown, context = {}) {
    const breakdown = normalizeTransportCostBreakdown(rawBreakdown);
    const goodsValue = resolveEffectiveTransportGoodsValue(breakdown, context);
    const converter = parseNumericInput(breakdown.converter);

    /** @type {Map<string, number>} */
    const amountsByKey = new Map();
    breakdown.lines.forEach((line) => {
        if (line.type === TRANSPORT_LINE_TYPE_PERCENT || line.enabled === false) {
            return;
        }
        const amount = resolveLineAmount(line, goodsValue, converter, amountsByKey);
        amountsByKey.set(line.key, amount);
    });

    const lineAmounts = breakdown.lines.map((line) => ({
        key: line.key,
        label: line.label,
        type: line.type,
        amount: resolveLineAmount(line, goodsValue, converter, amountsByKey),
    }));

    const total = roundMoney(
        lineAmounts.reduce((sum, line) => sum + line.amount, 0),
        4,
    );
    const rate = goodsValue > 0 && total > 0
        ? roundMoney(total / goodsValue, 6)
        : 0;

    return {
        goodsValue,
        converter,
        lineAmounts,
        total,
        rate,
    };
}

/**
 * @param {Record<string, unknown>} form
 * @returns {boolean}
 */
export function syncTransportCostFromBreakdown(form) {
    const context = {
        supplierListPriceNew: form.supplierListPriceNew,
    };
    let breakdown = normalizeTransportCostBreakdown(form.transportCostBreakdown);
    breakdown = applyAutoTransportGoodsValue(breakdown, context);
    if (breakdown.mode === TRANSPORT_COST_MODE_FORMULA) {
        ensureCustomsDutyLine(breakdown);
        breakdown.lines = orderTransportCostLines(breakdown.lines);
        ensurePercentFeeBases(breakdown);
    }
    form.transportCostBreakdown = breakdown;
    if (breakdown.mode !== TRANSPORT_COST_MODE_FORMULA) {
        return false;
    }

    const computed = calculateTransportCostBreakdown(breakdown, context);
    form.transportCostEstimated = computed.total > 0
        ? formatNumericInputValue(computed.total, 2)
        : '';
    if (!breakdown.rateIsManual) {
        form.transportCostRate = computed.rate > 0
            ? ratioToPercentageInput(computed.rate, 2)
            : '';
    }
    return true;
}

/**
 * @param {unknown} raw
 * @returns {Record<string, unknown>}
 */
export function serializeTransportCostBreakdown(raw) {
    const breakdown = normalizeTransportCostBreakdown(raw);
    return {
        mode: breakdown.mode,
        goodsValue: breakdown.goodsValue,
        goodsQuantity: breakdown.goodsQuantity,
        goodsDiscount: breakdown.goodsDiscount,
        goodsValueExtra: breakdown.goodsValueExtra,
        goodsValueIsManual: Boolean(breakdown.goodsValueIsManual),
        rateIsManual: Boolean(breakdown.rateIsManual),
        converter: breakdown.converter,
        converterFromCurrency: breakdown.converterFromCurrency,
        converterToCurrency: breakdown.converterToCurrency,
        lines: breakdown.lines.map((line) => ({
            key: line.key,
            label: line.label,
            type: line.type,
            value: line.value,
            baseGoodsValue: Boolean(line.baseGoodsValue),
            baseLineKeys: [...(line.baseLineKeys || [])],
            role: line.role || '',
            enabled: line.enabled !== false,
            sourceAmount: line.sourceAmount || '',
            sourceCurrency: line.sourceCurrency || '',
            converterUsed: line.converterUsed || '',
        })),
    };
}

